import React, { createContext, useState, useEffect, useContext, useRef } from 'react';
import { useLanguage } from './LanguageContext';
import logger from '../utils/logger';
import UsernameModal from '../components/UsernameModal';

const AuthContext = createContext();

/** DB/API may return votes as JSON string, object map, or array — keep login path consistent with fetchUserData. */
function normalizeUserVotes(raw) {
  if (raw == null || raw === '') return [];
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed == null ? [] : parsed;
    } catch {
      return [];
    }
  }
  if (typeof raw === 'object') {
    return raw;
  }
  return [];
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const { t } = useLanguage();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authStatus, setAuthStatus] = useState('initializing');
  const [googleLoginLoading, setGoogleLoginLoading] = useState(false);
  const loginInProgressRef = useRef(false);
  
  // Username modal state
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [pendingUserData, setPendingUserData] = useState(null);
  
  // Toast notification state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('error');

  // Show toast message function
  const showToastMessage = (message, type = 'error') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    
    setTimeout(() => {
      setShowToast(false);
    }, 5000);
  };

  // Simple user data fetch function (for existing users only)
  const fetchUserData = async (email, allowCreation = false) => {
    try {
      logger.log('🔍 Fetching user data for:', email, allowCreation ? '(allow creation)' : '(validation only)');
      
      const response = await fetch(`${window.location.origin}/.netlify/functions/user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email, 
          validationOnly: !allowCreation // If allowCreation is true, don't use validationOnly
        }),
      });
      
      if (!response.ok) {
        logger.error('❌ Failed to fetch user data:', response.statusText);
        return null;
      }
      
      const result = await response.json();
      
      if (result.success && result.data) {
        logger.log('✅ User data fetched successfully');
        logger.log('🔍 Raw user data from backend:', result.data);
        logger.log('🔍 Raw votes from backend:', result.data.votes);
        logger.log('🔍 Votes type:', typeof result.data.votes);
        const parsedVotes = normalizeUserVotes(result.data.votes);
        logger.log('🔍 Normalized votes:', parsedVotes);
        
        return {
          userData: result.data,
          votes: parsedVotes
        };
      }
      
      logger.log('❌ User not found in database');
      return null;
    } catch (error) {
      logger.error('❌ Error fetching user data:', error);
      return null;
    }
  };

  // Create or fetch user during login
  const createOrFetchUser = async (userData) => {
    try {
      logger.log('🔑 Creating or fetching user for login:', userData.email);
      
      const response = await fetch(`${window.location.origin}/.netlify/functions/user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: userData.email,
          name: userData.name || userData.given_name || userData.display_name,
          picture: userData.picture || userData.avatar_url,
          firstName: userData.given_name,
        }),
      });
      
      if (!response.ok) {
        logger.error('❌ Failed to create/fetch user:', response.statusText);
        return null;
      }
      
      const result = await response.json();
      
      if (result.success && result.data) {
        logger.log('✅ User created/fetched successfully');
        return {
          userData: result.data,
          votes: normalizeUserVotes(result.data.votes)
        };
      }
      
      logger.log('❌ Failed to create/fetch user');
      return null;
    } catch (error) {
      logger.error('❌ Error creating/fetching user:', error);
      return null;
    }
  };

  // Simple session check function
  const checkSession = async () => {
    try {
      logger.log('🔍 Checking session...');
      setAuthStatus('checking_session');
      
      const userEmail = localStorage.getItem('userEmail');
      const userPicture = localStorage.getItem('userPicture');
      
      if (userEmail) {
        logger.log('📧 Found email in localStorage:', userEmail);
        setAuthStatus('session_found');
        
        const result = await fetchUserData(userEmail, false);
        
        if (result && result.userData) {
          // Check if user is blocked
          if (result.userData.status === 1) {
            logger.log('🚫 User is blocked');
            setAuthStatus('user_blocked');
            clearUserData();
            showToastMessage('Cet utilisateur a été bloqué', 'error');
            return;
          }
          
          // Set user data
          const sessionUser = {
            ...result.userData,
            votes: result.votes || [],
            email: userEmail,
            picture: userPicture || result.userData.picture || null,
          };
          
          logger.log('🔍 Final session user object:', sessionUser);
          logger.log('🔍 Final session user votes:', sessionUser.votes);
          logger.log('🔍 Final session user votes type:', typeof sessionUser.votes);
          
          setUser(sessionUser);
          setAuthStatus('authenticated');
          logger.log('✅ Session restored successfully');
          
          // Check if JWT token exists for voting, warn if missing
          const jwtToken = localStorage.getItem('supabase.auth.token');
          if (!jwtToken) {
            console.warn('⚠️ JWT token missing - voting will not work');
            console.warn('⚠️ User needs to logout and login again to get JWT token');
            // Set a flag to show user they need to re-login for voting
            sessionUser.needsReLogin = true;
          } else {
            logger.log('✅ JWT token found - voting should work');
          }
        } else {
          if (loginInProgressRef.current) {
            logger.log('⏳ Login in progress — skipping session clear');
          } else {
            logger.log('❌ User not found in database, clearing session');
            setAuthStatus('session_invalid');
            localStorage.removeItem('supabase.auth.token');
            clearUserData();
          }
        }
      } else {
        logger.log('📭 No session found');
        setAuthStatus('no_session');
      }
    } catch (error) {
      logger.error('❌ Error during session check:', error);
      setAuthStatus('session_error');
      setUser(null);
    } finally {
      setLoading(false);
      logger.log('✅ Session check completed');
    }
  };

  // Clear user data function
  const clearUserData = () => {
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userPicture');
    localStorage.removeItem('userVotes');
    localStorage.removeItem('userReactions');
    localStorage.removeItem('supabase.auth.token'); // Remove JWT token for voting
    localStorage.setItem('boblegob_auth_state', 'logged_out');
    setUser(null);
  };

  // Update username function
  const updateUsername = async (email, newUsername) => {
    try {
      const response = await fetch(`${window.location.origin}/.netlify/functions/update-username`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email,
          username: newUsername
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update username');
      }
      
      const result = await response.json();
      
      if (result.success) {
        return true;
      } else {
        throw new Error(result.message || 'Failed to update username');
      }
    } catch (error) {
      logger.error('❌ Error updating username:', error);
      throw error;
    }
  };

  // Handle username submission from modal
  const handleUsernameSubmit = async (newUsername) => {
    try {
      if (!pendingUserData) {
        throw new Error('No pending user data');
      }
      
      logger.log('📝 Submitting username:', newUsername);
      
      // Update username in database
      await updateUsername(pendingUserData.email, newUsername);
      
      // Fetch updated user data
        const result = await fetchUserData(pendingUserData.email, false);
      
      if (result && result.userData) {
        // Generate session token for voting system
        if (pendingUserData.tokens && pendingUserData.tokens.id_token) {
          try {
            logger.log('🔐 Generating session token for voting system...');
            
            const sessionResponse = await fetch(`${window.location.origin}/.netlify/functions/generate-session`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                email: pendingUserData.email,
                googleIdToken: pendingUserData.tokens.id_token
              }),
            });
            
            if (sessionResponse.ok) {
              const sessionResult = await sessionResponse.json();
              if (sessionResult.success) {
                // Store session token for voting system
                const sessionData = {
                  currentSession: {
                    access_token: sessionResult.sessionToken,
                    expires_at: Date.now() + (24 * 60 * 60 * 1000), // 24 hours
                    token_type: 'Bearer'
                  }
                };
                
                localStorage.setItem('supabase.auth.token', JSON.stringify(sessionData));
                logger.log('✅ Session token generated and stored for voting');
              }
            }
          } catch (sessionError) {
            logger.error('❌ Error generating session token:', sessionError);
            console.warn('⚠️ Voting may not work without session token');
          }
        }
        
        const finalUser = {
          ...result.userData,
          email: pendingUserData.email,
          votes: result.votes || [],
          picture: pendingUserData.picture,
        };
        
        // Save to localStorage
        localStorage.setItem('userEmail', pendingUserData.email);
        localStorage.setItem('userPicture', pendingUserData.picture || '');
        localStorage.setItem('userVotes', JSON.stringify(finalUser.votes));
        localStorage.setItem('boblegob_auth_state', 'authenticated');
        
        setUser(finalUser);
        setAuthStatus('authenticated');
        setShowUsernameModal(false);
        setPendingUserData(null);
        stopGoogleLoading();
        logger.log('✅ Username updated and login completed');
        showToastMessage('Welcome! Your username has been set.', 'success');
      }
    } catch (error) {
      logger.error('❌ Error in username submission:', error);
      throw error;
    }
  };

  // Logout function
  const logout = () => {
    logger.log('🚪 Logging out...');
    setAuthStatus('logging_out');
    clearUserData();
    setAuthStatus('logged_out');
  };

  // Google login function (simplified)
  const handleGoogleLogin = async (userData, tokens = null) => {
    loginInProgressRef.current = true;
    try {
      logger.log('🔑 Google login attempt:', userData.email);
      setAuthStatus('authenticating');
      
      const result = await createOrFetchUser(userData);
      
      if (result && result.userData) {
        // Check if user is blocked
        if (result.userData.status === 1) {
          showToastMessage('Cet utilisateur a été bloqué', 'error');
          stopGoogleLoading();
          return false;
        }
        
        // Check if username is null or empty
        const hasUsername = result.userData.username && 
          result.userData.username.trim().length > 0;
        
        logger.log('👤 Username check:', {
          username: result.userData.username,
          hasUsername: hasUsername
        });
        
        // If no username, show modal
        if (!hasUsername) {
          logger.log('👤 No username set, showing username modal...');
          setPendingUserData({
            ...userData,
            tokens,
            dbUser: result.userData
          });
          setShowUsernameModal(true);
          // Don't stop loading yet, will stop after username is set
          return false;
        }
        
        // Generate session token for voting system
        if (tokens && tokens.id_token) {
          try {
            logger.log('🔐 Generating session token for voting system...');
            
            const sessionResponse = await fetch(`${window.location.origin}/.netlify/functions/generate-session`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                email: userData.email,
                googleIdToken: tokens.id_token
              }),
            });
            
            if (sessionResponse.ok) {
              const sessionResult = await sessionResponse.json();
              if (sessionResult.success) {
                // Store session token for voting system
                const sessionData = {
                  currentSession: {
                    access_token: sessionResult.sessionToken,
                    expires_at: Date.now() + (24 * 60 * 60 * 1000), // 24 hours
                    token_type: 'Bearer'
                  }
                };
                
                localStorage.setItem('supabase.auth.token', JSON.stringify(sessionData));
                logger.log('✅ Session token generated and stored for voting');
              } else {
                console.warn('⚠️ Session token generation failed, voting may not work');
              }
            } else {
              console.warn('⚠️ Session response not ok, voting may not work');
            }
          } catch (sessionError) {
            logger.error('❌ Error generating session token:', sessionError);
            console.warn('⚠️ Voting may not work without session token');
          }
        }
        
        const finalUser = {
          ...result.userData,
          email: userData.email,
          votes: result.votes || [],
          picture: userData.picture || result.userData.picture,
        };
        
        // Save to localStorage
        localStorage.setItem('userEmail', userData.email);
        localStorage.setItem('userPicture', userData.picture || '');
        localStorage.setItem('userVotes', JSON.stringify(finalUser.votes));
        localStorage.setItem('boblegob_auth_state', 'authenticated');
        
        setUser(finalUser);
        setAuthStatus('authenticated');
        stopGoogleLoading();
        logger.log('✅ Google login successful');
        return true;
      } else {
        logger.log('❌ Failed to create/fetch user during login');
        showToastMessage(t('errorCreatingAccount'), 'error');
        stopGoogleLoading();
        return false;
      }
    } catch (error) {
      logger.error('❌ Google login error:', error);
      setAuthStatus('error');
      showToastMessage(t('loginError'), 'error');
      stopGoogleLoading();
      return false;
    } finally {
      loginInProgressRef.current = false;
    }
  };

  // Refresh user data
  const refreshUserData = async () => {
    if (!user?.email) return false;
    
    try {
      const result = await fetchUserData(user.email, false); // Don't allow creation during refresh
      if (result && result.userData) {
        const updatedUser = {
          ...result.userData,
          votes: result.votes || [],
          email: user.email,
          picture: user.picture,
        };
        setUser(updatedUser);
        return true;
      }
      return false;
    } catch (error) {
      logger.error('❌ Error refreshing user data:', error);
      return false;
    }
  };

  // Google loading state functions
  const startGoogleLoading = () => {
    setGoogleLoginLoading(true);
  };

  const stopGoogleLoading = () => {
    setGoogleLoginLoading(false);
  };

  // Check if user needs to re-login for voting
  const needsReLoginForVoting = () => {
    if (!user) return false;
    const jwtToken = localStorage.getItem('supabase.auth.token');
    return !jwtToken || user.needsReLogin;
  };

  // Force user to re-login (for voting token)
  const forceReLoginForVoting = () => {
    logger.log('🔄 Forcing re-login for voting functionality');
    logout();
    showToastMessage('Please login again to enable voting', 'info');
  };

  // Check if JWT token is expired
  const isTokenExpired = (token) => {
    if (!token) return true;
    
    try {
      const tokenData = JSON.parse(token);
      const expiresAt = tokenData?.currentSession?.expires_at;
      
      if (!expiresAt) return true;
      
      // Check if token expires within the next 5 minutes (300000 ms)
      const fiveMinutesFromNow = Date.now() + (5 * 60 * 1000);
      return expiresAt < fiveMinutesFromNow;
    } catch (error) {
      logger.error('❌ Error checking token expiration:', error);
      return true;
    }
  };

  // Refresh JWT token automatically
  const refreshJWTToken = async () => {
    if (!user?.email) {
      console.warn('⚠️ Cannot refresh token - no user email');
      return null;
    }

    try {
      logger.log('🔄 Refreshing JWT token for:', user.email);

      let bearerForRefresh = null;
      const stored = localStorage.getItem('supabase.auth.token');
      if (stored) {
        try {
          bearerForRefresh = JSON.parse(stored)?.currentSession?.access_token || null;
        } catch (e) {
          /* ignore */
        }
      }

      const refreshResponse = await fetch(`${window.location.origin}/.netlify/functions/generate-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(bearerForRefresh ? { Authorization: `Bearer ${bearerForRefresh}` } : {})
        },
        body: JSON.stringify({
          email: user.email,
          refreshToken: true
        }),
      });
      
      if (refreshResponse.ok) {
        const refreshResult = await refreshResponse.json();
        if (refreshResult.success && refreshResult.sessionToken) {
          // Store new session token
          const sessionData = {
            currentSession: {
              access_token: refreshResult.sessionToken,
              expires_at: Date.now() + (24 * 60 * 60 * 1000), // 24 hours
              token_type: 'Bearer'
            }
          };
          
          localStorage.setItem('supabase.auth.token', JSON.stringify(sessionData));
          logger.log('✅ JWT token refreshed successfully');
          return refreshResult.sessionToken;
        }
      }
      
      console.warn('⚠️ Failed to refresh JWT token');
      return null;
    } catch (error) {
      logger.error('❌ Error refreshing JWT token:', error);
      return null;
    }
  };

  // Get valid JWT token (auto-logout if expired and can't refresh)
  const getValidJWTToken = async () => {
    try {
      const existingToken = localStorage.getItem('supabase.auth.token');

      if (!existingToken) {
        logger.log('🔄 No JWT token found, attempting rotation...');
        return await refreshJWTToken();
      }

      if (isTokenExpired(existingToken)) {
        logger.log('🔄 JWT token expired or near expiry, attempting rotation...');
        const newToken = await refreshJWTToken();
        if (newToken) return newToken;
        logger.log('⚠️ Session rotation failed — log in again to use protected actions');
        return null;
      }

      const tokenData = JSON.parse(existingToken);
      const accessToken = tokenData?.currentSession?.access_token;

      if (accessToken) {
        logger.log('✅ Using existing valid JWT token');
        return accessToken;
      }

      logger.log('🔄 Invalid token format, attempting rotation...');
      return await refreshJWTToken();
    } catch (error) {
      logger.error('❌ Error getting valid JWT token:', error);
      return await refreshJWTToken();
    }
  };

  // Force clear function for debugging
  const forceUserClear = () => {
    logger.log('🧹 Force clearing all user data');
    clearUserData();
    setAuthStatus('logged_out');
  };

  // Debug function
  const debugAuthState = () => {
    logger.log('🔍 AUTH DEBUG STATE:');
    logger.log('User:', user);
    logger.log('Loading:', loading);
    logger.log('Auth Status:', authStatus);
    logger.log('LocalStorage userEmail:', localStorage.getItem('userEmail'));
    logger.log('LocalStorage userPicture:', localStorage.getItem('userPicture'));
    logger.log('LocalStorage userVotes:', localStorage.getItem('userVotes'));
    logger.log('LocalStorage authState:', localStorage.getItem('boblegob_auth_state'));
  };

  if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
    window.debugAuth = debugAuthState;
    window.forceUserClear = forceUserClear;
  }

  // Initial session check on mount
  useEffect(() => {
    logger.log('🚀 AuthProvider mounted, checking session...');
    checkSession();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for storage events from other tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'userEmail' || e.key === 'boblegob_auth_state') {
        const currentEmail = localStorage.getItem('userEmail');
        
        if (currentEmail && !user) {
          // We should be logged in
          checkSession();
        } else if (!currentEmail && user) {
          // We should be logged out
          setUser(null);
          setAuthStatus('logged_out');
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const value = {
    user,
    setUser,
    loading,
    authStatus,
    handleGoogleLogin,
    googleLoginLoading,
    startGoogleLoading,
    stopGoogleLoading,
    logout,
    refreshUserData,
    needsReLoginForVoting,
    forceReLoginForVoting,
    forceUserClear,
    isAuthenticated: !!user,
    showToast,
    toastMessage,
    toastType,
    showToastMessage,
    // JWT refresh functions
    getValidJWTToken,
    refreshJWTToken,
    isTokenExpired
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      
      {/* Username Modal */}
      <UsernameModal
        isOpen={showUsernameModal}
        onClose={() => {
          setShowUsernameModal(false);
          setPendingUserData(null);
          stopGoogleLoading();
        }}
        onSubmit={handleUsernameSubmit}
        currentUsername=""
      />
      
      {/* Toast Notification */}
      {showToast && (
        <div className={`fixed top-4 right-4 z-[9999] transform transition-all duration-500 ease-in-out ${
          showToast ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
        }`}>
          <div className={`flex items-center p-4 rounded-lg shadow-lg min-w-[300px] max-w-md ${
            toastType === 'success' 
              ? 'bg-green-600 text-white' 
              : 'bg-red-600 text-white'
          }`}>
            <div className="flex-1">
              <p className="font-medium">{toastMessage}</p>
            </div>
            <button
              onClick={() => setShowToast(false)}
              className="ml-4 text-white hover:text-gray-200 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};

export { AuthContext };
export default AuthProvider;
